import { observer } from 'mobx-react';
import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  Button,
  Alert,
  ButtonType,
  Steps,
  Divider,
  Form,
  notification,
} from 'syngenta-digital-cropwise-react-ui-kit';
import { Typography, Input } from 'antd';
import { capitalize } from 'lodash';
import { RoutePaths } from '../../routes/history';
import IntegrationsModel from '../Integrations/IntegrationsModel';
import { IntegrationErrorTypes } from '../Integrations/types';
import PartnerModule from 'models/PartnerModule';
import LoadingView from './LoadingView';
import { isEmptyOrSpaces } from 'services/utils';
import { USER_ORG_ID } from 'models/Integration';
import { WizardStep } from 'models';
import { WizardStepSubTitle } from 'views/Integrations/subviews/WizardStepSubTitle';
import { integrateUser } from 'models/Valet';
import { authenticationStep } from 'resources/IntegrationsData';
import { useTranslation } from 'react-i18next';
import { usePartnerIdParam } from '../Integrations/hooks/usePartnerIdParam';
import { BasicAuthWizardArticle } from './components/BasicAuthWizardArticle';
import { useSortedWizardSteps } from './hooks/useSortedWizardSteps';

const BasicAuthView = ({ viewModel }: { viewModel: IntegrationsModel }) => {
  const { Title } = Typography;
  const { Step } = Steps;
  const { Password } = Input;
  const userId = viewModel._activeUser;
  const { integration_key } = useParams<{ integration_key: string }>();
  const partner_id = usePartnerIdParam();
  const history = useHistory();
  const [userInputValue, setUserInputValue] = React.useState<string>('');
  const [passwordInputValue, setPasswordInputValue] = React.useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const viewModelRef = useRef(viewModel);
  const { t } = useTranslation();

  const steps = useSortedWizardSteps(viewModel, viewModelRef, history);

  const submitData = async (userName: string, password: string) => {
    setIsLoading(true);
    try {
      if (!viewModel.activeIntegration) {
        await viewModel.setActiveIntegrationByIntegrationKey(integration_key);
      }
      window.localStorage.setItem(USER_ORG_ID, viewModel.activeUser);
      return await PartnerModule.get_validation_token(partner_id, userName, password);
    } catch (err) {
      console.error(err);
      setIsLoading(false);
      viewModel.new_notification(IntegrationErrorTypes.AUTHENTICATION_ERROR);
      return err;
    }
  };

  const handleSubmit = async () => {
    let response: any = {};
    setIsLoading(true);
    try {
      response = await submitData(userInputValue, passwordInputValue);
      if (response.error_type) {
        setIsLoading(false);
        return;
      }
      viewModel.active_integration_token = response;

      if (viewModel.activeIntegration) {
        await integrateUser(
          userId,
          viewModel.activeIntegration,
          viewModel.active_integration_token,
        );
        viewModel.activeIntegration.is_configured = viewModel.active_integration_token;
        await viewModel.initialize_integrations();
      }
      setIsLoading(false);
      if (!viewModel.activeIntegration?.wizardSteps) {
        viewModel.new_notification(IntegrationErrorTypes.INTEGRATION_SUCCESS);
      }
      history.push(RoutePaths.ConfigureIntegrationWizardView(partner_id).toString());
    } catch (error) {
      console.error(error);
      setIsLoading(false);
      notification.error({
        message: 'unable to save integration based on your data',
        description: '',
        placement: 'topRight',
      });
    }
  };
  const translatedLogin = t('Login with your');
  const translatedCreds = t('credentials');
  return (
    <div className="integration-config h-screen p-8 overflow-auto">
      {isLoading ? (
        <LoadingView dontUseFullScreen={true} />
      ) : (
        viewModel.activeIntegration && (
          <BasicAuthWizardArticle
            t={t}
            steps={steps}
            partner_id={partner_id}
            translatedLogin={translatedLogin}
            translatedCreds={translatedCreds}
            userInputValue={userInputValue}
            setUserInputValue={setUserInputValue}
            passwordInputValue={passwordInputValue}
            setPasswordInputValue={setPasswordInputValue}
            isEmptyOrSpaces={isEmptyOrSpaces}
            handleSubmit={handleSubmit}
            history={history}
            RoutePaths={RoutePaths}
            PartnerModule={PartnerModule}
          />
        )
      )}
    </div>
  );
};

export default observer(BasicAuthView);
